import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'rs_auth_token';

const AuthContext = createContext(null);

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' · ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [bootstrapping, setBootstrapping] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  // attach token to axios
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      if (token && config.url && config.url.startsWith(BACKEND_URL)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [token]);

  // bootstrap session
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) { setBootstrapping(false); return; }
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setUser(res.data);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const persistAuth = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
  };

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    persistAuth(res.data);
    return res.data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await axios.post(`${API}/auth/register`, payload);
    // Pending users receive no token — DO NOT auto-login them.
    if (res.data && res.data.token) {
      persistAuth(res.data);
    }
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const forgotInit = useCallback(async (email) => {
    const res = await axios.post(`${API}/auth/forgot-password/init`, { email });
    return res.data.security_question;
  }, []);

  const forgotVerify = useCallback(async (payload) => {
    const res = await axios.post(`${API}/auth/forgot-password/verify`, payload);
    persistAuth(res.data);
    return res.data.user;
  }, []);

  const fetchSecurityQuestions = useCallback(async () => {
    const res = await axios.get(`${API}/auth/security-questions`);
    return res.data.questions;
  }, []);

  const value = {
    user,
    token,
    bootstrapping,
    isAuthenticated: Boolean(user),
    isAdmin: Boolean(user && user.role === 'admin'),
    login,
    register,
    logout,
    forgotInit,
    forgotVerify,
    fetchSecurityQuestions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
